import Markdown from '@/components/dataDisplay/Markdown';
import Tooltip from '@/components/dataDisplay/Tooltip';
import { AMPLITUDE_ACTIONS } from '@/modules/core/amplitude/amplitude';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import areAllQuestionsAnswered from '@/modules/core/chatbot/areAllQuestionsAnswered';
import findNextQuestionToAsk from '@/modules/core/chatbot/findNextQuestionToAsk';
import findStartingQuestion from '@/modules/core/chatbot/findStartingQuestion';
import getQuestionsToAsk, { ChatbotQuestions } from '@/modules/core/chatbot/getQuestionsToAsk';
import { shouldAnimateCurrentQuestion } from '@/modules/core/chatbot/shouldAnimateCurrentQuestion';
import useCustomer from '@/modules/core/data/hooks/useCustomer';
import { hasValue } from '@/modules/core/data/record';
import { AirtableRecord } from '@/modules/core/data/types/AirtableRecord';
import { Campus } from '@/modules/core/data/types/Campus';
import ChatbotQuestion from '@/modules/core/data/types/CDE/ChatbotQuestion';
import { ChatbotContext } from '@/modules/core/data/types/ChatbotContext';
import { Customer } from '@/modules/core/data/types/Customer';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import {
  ReactSelectDefaultOption,
  ReactSelectGroup,
} from '@/modules/core/reactSelect/types/ReactSelect';
import useUserSession, { UserSession } from '@/modules/core/userSession/useUserSession';
import { flattenStepsFromEducationalPrograms } from '@/modules/solutions/educationalProgramStep';
import {
  filterStudentSolutions,
  getDefaultContext,
} from '@/modules/solutions/solutionsFilters';
import { serializeStudentSolutionContext } from '@/modules/solutions/studentSolutionContext';
import { Amplitude } from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import filter from 'lodash.filter';
import find from 'lodash.find';
import get from 'lodash.get';
import isEqual from 'lodash.isequal';
import map from 'lodash.map';
import size from 'lodash.size';
import sortBy from 'lodash.sortby';
import React, {
  Fragment,
  useState,
} from 'react';
import {
  Trans,
  useTranslation,
} from 'react-i18next';
import Question, { QuestionProps } from './Question';
import SingleSelectInput from './SingleSelectInput';
import SolutionsFoundCard from './SolutionsFoundCard';

type Props = {
  isInIframe: boolean;
}

/**
 * Displays the whole chatbot dialog.
 *
 * Guides the end-user through questions and choices in order to build a profile "context".
 *
 * XXX This is most likely the worst part of the app and the most complicated.
 *  The chatbot workflow is hardcoded and every edge case is handled manually.
 *  The overall complexity is very high.
 */
const ChatbotDialog: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    isInIframe,
  } = props;
  const customerRef = process.env.NEXT_PUBLIC_CUSTOMER_REF;
  const theme: CustomerTheme = useTheme();
  const { t } = useTranslation();
  const customer: Customer = useCustomer();
  const { modChatbot: chatbotSettings } = customer;
  const { primaryColor, onPrimaryColor, primaryColorVariant1, surfaceColor } = theme;
  const bubbleTimerDuration: number = process.env.APP_STAGE === 'development' ? 100 : 1500; // Avoid wasting time in dev mode
  const updatedQuestionAnimationDuration = 200; // Duration of all animations that are performed, when a question's choice is updated
  const allStudentSolutions: AirtableRecord<StudentSolution>[] = customer?.studentSolutions;
  const allEducationalPrograms: AirtableRecord<EducationalProgram>[] = sortBy(customer?.educationalPrograms, 'label');
  const allEducationalProgramSteps: AirtableRecord<EducationalProgramStep>[] = sortBy(flattenStepsFromEducationalPrograms(allEducationalPrograms), 'label');
  const allCampuses: AirtableRecord<Campus>[] = customer?.campuses;
  const userSession: UserSession = useUserSession();
  const [context, setContext] = useState<ChatbotContext>(getDefaultContext({
    customerRef: customerRef,
    persona: userSession?.persona,
  }));
  // console.debug('chatbot context', context);

  const questions: ChatbotQuestions = getQuestionsToAsk(context, t, customer, allEducationalPrograms, allEducationalProgramSteps, allCampuses, allStudentSolutions);
  const {
    personaQuestion,
    programsQuestion,
    programStepsQuestion,
    campusesQuestion,
  } = questions;
  const [activeQuestion, setActiveQuestion] = useState<ChatbotQuestion>(findStartingQuestion(context, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses));
  const studentSolutionsFilteredByContext: StudentSolution[] = filterStudentSolutions(allStudentSolutions, context);
  const [updatedQuestion, setUpdatedQuestion] = useState<{ order: number }>(null); // Helps handling animations, when a choice is changed
  const shouldDisplaySolutionsFound: boolean = areAllQuestionsAnswered(context, allEducationalPrograms, allEducationalProgramSteps, allCampuses);
  // console.debug('activeQuestion', activeQuestion);

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
        question: { ref: activeQuestion.ref },
        filters: serializeStudentSolutionContext(context), // Serialize to avoid crash due to circular references (also, we just want identifiable data, not a huge dataset)
      })}
      userProperties={{
        persona: context.persona,
      }}
      // Use debounce so that the nested logEvent() calls will include all context data, otherwise the even will tick before the context is actually updated
      debounceInterval={400}
    >
      {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
        <div
          css={css`
            width: 100%;

            * {
              clear: both;
            }

            .message-container {
              overflow: scroll;
              -ms-overflow-style: none;
              border-radius: 15px 15px 0px 0px;
              width: 60%;
              margin: auto;
              min-height: 92vh;
              height: auto;
              padding-top: 20px;

              &.solutions-not-found {
                padding-bottom: 300px;
              }
            }

            .message-container::-webkit-scrollbar {
              display: none;
            }

            .message-gradient-scroll {
              position: absolute;
              border-radius: 15px 15px 0px 0px;
              z-index: 1;
              background: rgb(255, 255, 255);
              background: linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
              height: 90px;
              width: 60%;
            }

            .message {
              background-color: ${surfaceColor};
              border-radius: 25px 25px 25px 0px;
              padding: 12px 20px 8px 20px;
              margin: 5px;
              max-width: 60%;
              float: left;

              &.user {
                float: right;
                color: ${onPrimaryColor};
                background-color: ${primaryColor};
                border-radius: 25px 25px 0px 25px;

                & b {
                  font-weight: bold;
                }
              }
            }

            .select-input {
              width: 60%;
            }

            .close-message {
              color: grey;
              background-color: white;
              float: right;
              position: relative;
              font-size: 2rem;
              right: -20px;
              top: -25px;
              border-radius: 30px;
            }

            .text-spacing {
              text-align: center;
              margin-top: 200px;
              margin-bottom: 100px;
            }

            .select-group-items-counter {
              float: right;
              font-weight: bold;
            }

            .select-group-label {
              background-color: #D9D9D9;
              padding: 3px 10px 3px 10px;
              margin-top: 15px;
              border-radius: 20px;
            }

            .option-button {
              border: none;
              color: ${primaryColor};
              background-color: ${primaryColorVariant1};
              margin-top: 4px;
              margin-right: 5px;
              border-radius: 30px;
              padding: 12px 20px 8px 20px;
              font-size: 15px;
            }

            @media (max-width: 991.98px) {
              .message-container {
                width: 90%;
              }

              .message {
                max-width: 90%;
              }

              .select-input {
                width: 90%;
              }

              .message-fading {
                width: 90%;
              }
            }
          `}
        >
          <div
            className={classnames('message-container', {
              'solutions-found': shouldDisplaySolutionsFound,
              'solutions-not-found': !shouldDisplaySolutionsFound,
            })}
          >
            <Question
              question={null}
              activeQuestion={activeQuestion}
              context={context}
              // XXX Bypass the "bubbleTimer" when the active question isn't "persona", because that means we're loading the chatbot while already knowing the user's persona
              bubbleTimerDuration={activeQuestion === personaQuestion ? bubbleTimerDuration : 0}
            >
              <Fragment>
                {
                  // Use customer content first, then fallback to default content (Locize)
                  hasValue(chatbotSettings, 'dialogIntroduction') ? (
                    <Markdown
                      className={'message'}
                      text={chatbotSettings?.dialogIntroduction}
                    />
                  ) : (
                    <div className={'message'}>
                      <p>
                        <Trans i18nKey={'chatbotPage.dialog.question.welcome'}>
                          Bienvenue ! Je vais vous aider à solutionner vos études à <strong>{{ label: customer?.label }}</strong>.
                        </Trans>
                      </p>
                    </div>
                  )
                }

                <Question
                  question={personaQuestion}
                  activeQuestion={activeQuestion}
                  context={context}
                  // XXX Bypass the "bubbleTimer" when the active question isn't "persona", because that means we're loading the chatbot while already knowing the user's persona
                  bubbleTimerDuration={activeQuestion === personaQuestion ? bubbleTimerDuration : 0}
                >
                  {
                    (props: QuestionProps): JSX.Element => {
                      const {
                        question,
                      } = props;

                      return (
                        <Fragment>
                          <div
                            className={'message'}
                          >
                            <div
                              dangerouslySetInnerHTML={{
                                __html: question.label,
                              }}
                            />
                            <div
                            >
                              {
                                map(filter(question.choices, (choice: { value: string }): boolean => {
                                  // Filter out the selected choice, when a choice is selected
                                  return choice.value !== context[question.ref];
                                }), (choice: ReactSelectDefaultOption, i: number) => {
                                  const { value, label } = choice;

                                  return (
                                    <button
                                      key={i}
                                      className={classnames('option-button')}
                                      onClick={(): void => {
                                        const nextContext: ChatbotContext = {
                                          ...context,
                                          [question.ref]: value,
                                        };

                                        // Update the active question only if it's the first time the user answers (not an update)
                                        // If it's an update, we don't change the active question but rather keep the dialog where it was left of (because this answer doesn't impact other questions/answers)
                                        if (activeQuestion === question) {
                                          setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, setContext));
                                        } else {
                                          setContext(nextContext);
                                        }

                                        // Handling animation of other questions that need to be "refreshed" when this answer changes
                                        setUpdatedQuestion(question);
                                        setTimeout(() => {
                                          setUpdatedQuestion(null);
                                        }, updatedQuestionAnimationDuration);

                                        logEvent(`chatbot-answer`, {
                                          question: {
                                            ref: question.ref,
                                            inputType: 'quick-replies',
                                            isMultiChoices: false,
                                            rank: 1,
                                          },
                                          selection: {
                                            ref: value,
                                          },
                                          action: AMPLITUDE_ACTIONS.SELECT,
                                        });
                                      }}
                                      dangerouslySetInnerHTML={{ __html: label }}
                                    />
                                  );
                                })
                              }
                            </div>
                          </div>
                          {
                            context[question.ref] && (
                              <div
                                className={classnames('message user', { [`animated fadeIn delay-100ms duration-${updatedQuestionAnimationDuration}ms`]: shouldAnimateCurrentQuestion(updatedQuestion, question) })}
                              >
                                <span
                                  // @ts-ignore
                                  dangerouslySetInnerHTML={{ __html: get(find(question.choices, { value: context[question.ref] }), 'label') }}
                                />
                                <FontAwesomeIcon
                                  icon={'times-circle'}
                                  className={'close-message'}
                                  css={css`
                                    cursor: pointer;
                                  `}
                                  onClick={(): void => {
                                    const nextContext: ChatbotContext = {
                                      ...context,
                                      [question.ref]: undefined,

                                      // Also reset the answers given to the next questions (to avoid state mismatch)
                                      [programsQuestion.ref]: undefined,
                                      [programStepsQuestion.ref]: undefined,
                                    };
                                    setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, setContext));

                                    logEvent(`chatbot-answer`, {
                                      question: {
                                        ref: question.ref,
                                        inputType: 'quick-replies',
                                        isMultiChoices: false,
                                        rank: 1,
                                      },
                                      selection: {
                                        ref: context[question.ref]?.value,
                                      },
                                      action: AMPLITUDE_ACTIONS.REMOVE,
                                    });
                                  }}
                                />
                              </div>
                            )
                          }
                        </Fragment>
                      );
                    }
                  }
                </Question>
              </Fragment>
            </Question>

            <Question
              question={programsQuestion}
              activeQuestion={activeQuestion}
              context={context}
              bubbleTimerDuration={bubbleTimerDuration}
            >
              {
                (props: QuestionProps): JSX.Element => {
                  const {
                    question,
                  } = props;

                  return (
                    <Fragment>
                      <div
                        className={'message'}
                      >
                        {question.label}
                      </div>
                      <div
                        className={'message user select-input'}
                      >
                        <SingleSelectInput
                          question={question}
                          selectedValue={context.educationalPrograms?.[0]}
                          onChange={(selectedChoice: EducationalProgram): void => {
                            const newSelectedChoices: EducationalProgram[] = [selectedChoice];

                            if (!isEqual(newSelectedChoices, context[question.ref])) {
                              const nextContext: ChatbotContext = {
                                ...context,
                                [question.ref]: newSelectedChoices,
                                [programStepsQuestion.ref]: undefined, // Forget about the selected programSteps, because we changed the selected program and they depend on it
                                [campusesQuestion.ref]: undefined, // Forget about the selected campuses, because they depend on the selected programSteps
                              };

                              // Display next question
                              setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, setContext));

                              setUpdatedQuestion(question);
                              setTimeout(() => {
                                setUpdatedQuestion(null);
                              }, updatedQuestionAnimationDuration);

                              logEvent(`chatbot-answer`, {
                                question: {
                                  ref: question.ref,
                                  inputType: 'select',
                                  isMultiChoices: false,
                                  rank: 2,
                                },
                                selection: {
                                  ref: selectedChoice.ref,
                                },
                                action: AMPLITUDE_ACTIONS.SELECT,
                              });
                            }
                          }}
                          theme={theme}
                        />
                      </div>
                    </Fragment>
                  );
                }
              }
            </Question>

            <Question
              question={programStepsQuestion}
              activeQuestion={activeQuestion}
              context={context}
              bubbleTimerDuration={bubbleTimerDuration}
            >
              {
                (props: QuestionProps): JSX.Element => {
                  const {
                    question,
                  } = props;

                  return (
                    <div className={classnames({ [`animated fadeIn delay-100ms duration-${updatedQuestionAnimationDuration}ms`]: shouldAnimateCurrentQuestion(updatedQuestion, question) })}>
                      <div
                        className={classnames('message')}
                      >
                        {question.label}
                      </div>
                      <div
                        className={classnames('message user select-input')}
                      >
                        <SingleSelectInput
                          question={question}
                          selectedValue={context.educationalProgramSteps?.[0]}
                          getOptionLabel={(step: EducationalProgramStep): string => {
                            return step?.label;
                          }}
                          onChange={(selectedChoice: EducationalProgramStep): void => {
                            const newSelectedChoices: EducationalProgramStep[] = [selectedChoice];

                            if (!isEqual(newSelectedChoices, context[question.ref])) {
                              const nextContext: ChatbotContext = {
                                ...context,
                                [question.ref]: newSelectedChoices,
                                [campusesQuestion.ref]: undefined, // Forget about the selected campuses, because we changed the selected programSteps and they depend on it
                              };

                              setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, setContext));

                              setUpdatedQuestion(question);
                              setTimeout(() => {
                                setUpdatedQuestion(null);
                              }, updatedQuestionAnimationDuration);

                              logEvent(`chatbot-answer`, {
                                question: {
                                  ref: question.ref,
                                  inputType: 'select',
                                  isMultiChoices: false,
                                  rank: 3,
                                },
                                selection: {
                                  ref: selectedChoice.ref,
                                },
                                action: AMPLITUDE_ACTIONS.SELECT,
                              });
                            }
                          }}
                          formatGroupLabel={(data: ReactSelectGroup): JSX.Element => {
                            const {
                              label: programLabel,
                              labelShort: programLabelShort,
                              options: steps,
                            } = data;

                            return (
                              <div>
                                {
                                  // Display label short if available (with tooltip to help remember what program it means)
                                  programLabelShort ? (
                                    <Tooltip overlay={<span>{programLabel}</span>}>
                                      <span className={'select-group-label'}>
                                        {programLabelShort}
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <span className={'select-group-label'}>
                                      {programLabel}
                                    </span>
                                  )
                                }

                                <span className={'select-group-items-counter'}>
                                  {size(steps)}
                                </span>
                              </div>
                            );
                          }}
                          theme={theme}
                        />
                      </div>
                    </div>
                  );
                }
              }
            </Question>

            <Question
              question={campusesQuestion}
              activeQuestion={activeQuestion}
              context={context}
              bubbleTimerDuration={bubbleTimerDuration}
            >
              {
                (props: QuestionProps): JSX.Element => {
                  const {
                    question,
                  } = props;

                  return (
                    <Fragment>
                      <div
                        className={'message'}
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: question.label,
                          }}
                        />
                        <div
                        >
                          {
                            map(filter(question.choices, (choice: Campus): boolean => {
                              // Filter out the selected choice, when a choice is selected
                              return !find(context[question.ref], { ref: choice.ref });
                            }), (choice: Campus, i: number) => {
                              const { label } = choice;

                              return (
                                <button
                                  key={i}
                                  className={classnames('option-button')}
                                  onClick={(): void => {
                                    const { ref } = choice;
                                    const selectedChoices: Campus[] = [choice];

                                    const nextContext: ChatbotContext = {
                                      ...context,
                                      [question.ref]: selectedChoices,
                                    };

                                    // Update the active question only if it's the first time the user answers (not an update)
                                    // If it's an update, we don't change the active question but rather keep the dialog where it was left of (because this answer doesn't impact other questions/answers)
                                    if (activeQuestion === question) {
                                      setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, setContext));
                                    } else {
                                      setContext(nextContext);
                                    }

                                    // Handling animation of other questions that need to be "refreshed" when this answer changes
                                    setUpdatedQuestion(question);
                                    setTimeout(() => {
                                      setUpdatedQuestion(null);
                                    }, updatedQuestionAnimationDuration);

                                    logEvent(`chatbot-answer`, {
                                      question: {
                                        ref: question.ref,
                                        inputType: 'quick-replies',
                                        isMultiChoices: false,
                                        rank: 1,
                                      },
                                      selection: {
                                        ref: ref,
                                      },
                                      action: AMPLITUDE_ACTIONS.SELECT,
                                    });
                                  }}
                                  dangerouslySetInnerHTML={{ __html: label }}
                                />
                              );
                            })
                          }
                        </div>
                      </div>
                      {
                        context[question.ref] && (
                          <div
                            className={classnames('message user', { [`animated fadeIn delay-100ms duration-${updatedQuestionAnimationDuration}ms`]: shouldAnimateCurrentQuestion(updatedQuestion, question) })}
                          >
                            <span
                              // @ts-ignore
                              dangerouslySetInnerHTML={{ __html: get(find(question.choices, { ref: context[question.ref][0].ref }), 'label') }}
                            />
                            <FontAwesomeIcon
                              icon={'times-circle'}
                              className={'close-message'}
                              css={css`
                                cursor: pointer;
                              `}
                              onClick={(): void => {
                                const nextContext: ChatbotContext = {
                                  ...context,
                                  [question.ref]: undefined,
                                };
                                setActiveQuestion(findNextQuestionToAsk(question, nextContext, questions, allEducationalPrograms, allEducationalProgramSteps, allCampuses, setContext));

                                logEvent(`chatbot-answer`, {
                                  question: {
                                    ref: question.ref,
                                    inputType: 'quick-replies',
                                    isMultiChoices: false,
                                    rank: 1,
                                  },
                                  selection: {
                                    ref: context[question.ref]?.[0]?.ref,
                                  },
                                  action: AMPLITUDE_ACTIONS.REMOVE,
                                });
                              }}
                            />
                          </div>
                        )
                      }
                    </Fragment>
                  );
                }
              }
            </Question>

            {
              shouldDisplaySolutionsFound && (
                <SolutionsFoundCard
                  chatbotContext={context}
                  studentSolutionsFilteredByContext={studentSolutionsFilteredByContext}
                  className={classnames({ [`animated fadeIn delay-100ms duration-${updatedQuestionAnimationDuration}ms`]: shouldAnimateCurrentQuestion(updatedQuestion, { order: Infinity }) })}
                  isInIframe={isInIframe}
                />
              )
            }
          </div>
        </div>
      )}
    </Amplitude>
  );
};

export default ChatbotDialog;
